/**
 * ColumnMappingDialog - extracted from BankFeedCenterPage (Phase 4).
 *
 * Shown when an uploaded statement's columns can't be detected automatically.
 * The user picks which column holds each field (date/description/amounts).
 *
 * On apply, we POST the mapping back to the same job id and tell the parent
 * to start polling for completion via its background-jobs hook.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Table as TableIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_DEFS = [
  { key: 'date_col', label: 'Date *', required: true },
  { key: 'description_col', label: 'Description *', required: true },
  { key: 'debit_col', label: 'Debit/Withdrawal' },
  { key: 'credit_col', label: 'Credit/Deposit' },
  { key: 'amount_col', label: 'Single Amount' },
  { key: 'balance_col', label: 'Balance' },
  { key: 'reference_col', label: 'Reference' },
];

export const ColumnMappingDialog = ({
  open,
  job,                  // { jobId, filename, rawPreview: { headers, sample_rows } }
  columnMapping,
  onColumnMappingChange,
  onClose,
  onApplied,            // (jobMeta) => void: parent uses this to add to backgroundJobs
}) => {
  const [applying, setApplying] = useState(false);

  if (!job) return null;

  const canApply =
    columnMapping.date_col !== null &&
    columnMapping.description_col !== null &&
    (columnMapping.debit_col !== null || columnMapping.credit_col !== null || columnMapping.amount_col !== null);

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await axios.post(`${API}/api/bank-feeds/apply-column-mapping/${job.jobId}`, columnMapping);
      if (res.data?.success) {
        toast.success(`Processing ${res.data.total_transactions} transactions with your mapping…`);
        onApplied?.({
          jobId: job.jobId,
          filename: job.filename,
          startedAt: Date.now(),
          progress: 15,
          status: 'processing',
          totalTx: res.data.total_transactions,
          processedTx: 0,
        });
        onClose?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to apply column mapping');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="column-mapping-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TableIcon className="w-5 h-5 text-amber-600" />
            Manual Column Mapping
          </DialogTitle>
          <DialogDescription>
            We couldn't automatically detect the columns in <strong>{job.filename}</strong>.
            Please tell us which column contains each piece of data.
          </DialogDescription>
        </DialogHeader>

        {job.rawPreview && (
          <div className="space-y-4">
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-100">
                    {job.rawPreview.headers.map((h, i) => (
                      <th key={i} className="px-2 py-1.5 text-left font-medium border-b whitespace-nowrap">
                        Col {i}: {h || '(empty)'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {job.rawPreview.sample_rows?.slice(0, 5).map((row, ri) => (
                    <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      {row.map((cell, ci) => (
                        <td key={ci} className="px-2 py-1 border-b text-slate-600 whitespace-nowrap max-w-[150px] truncate">
                          {cell || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {FIELD_DEFS.map(({ key, label }) => (
                <div key={key} className="space-y-1">
                  <label className="text-xs font-medium text-slate-700">{label}</label>
                  <select
                    data-testid={`mapping-${key}`}
                    className="w-full border rounded px-2 py-1.5 text-sm bg-white"
                    value={columnMapping[key] ?? ''}
                    onChange={(e) =>
                      onColumnMappingChange((prev) => ({
                        ...prev,
                        [key]: e.target.value === '' ? null : parseInt(e.target.value),
                      }))
                    }
                  >
                    <option value="">-- Not mapped --</option>
                    {job.rawPreview.headers.map((h, i) => (
                      <option key={i} value={i}>Col {i}: {h || '(empty)'}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <p className="text-xs text-amber-600">
              * Date and Description are required. You need at least one of: Debit/Credit columns OR a single Amount column.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} data-testid="column-mapping-cancel">
                Cancel
              </Button>
              <Button
                data-testid="apply-mapping-btn"
                disabled={applying || !canApply}
                onClick={handleApply}
              >
                {applying ? 'Applying…' : 'Apply Mapping & Process'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ColumnMappingDialog;
